import { useMutation, useQuery } from '@tanstack/react-query';

import { queryClient } from '~app/query-client';
import { baseAPI } from '~shared/api/base-api';
import { KEYS, QUERY_KEYS } from '~shared/api/query-keys';

export interface PrivateLessonSlot {
  startTime: string;
  endTime: string;
  date: string;
}

export interface PrivateLessonBooking {
  _id: string;
  offeringId:
    | string
    | {
        _id: string;
        title: string;
        tuitionFee?: number;
        duration?: number;
      };
  studentId:
    | string
    | {
        _id: string;
        firstName?: string;
        lastName?: string;
        name?: string;
      };
  startTime: string;
  endTime: string;
  timezone?: string;
  paymentStatus: 'pending' | 'paid' | 'failed';
  status: 'scheduled' | 'completed' | 'cancelled';
  invoiceId?: string | { _id: string; status: string; finalAmount: number };
  seriesId?: string;
  seriesIndex?: number;
  recurrenceLabel?: string;
}

export interface GetPrivateLessonSlotsParams {
  offeringId: string;
  startDate: string;
  endDate: string;
}

export interface GetPrivateLessonBookingsParams {
  offeringId?: string;
  studentId?: string;
  seriesId?: string;
  timing?: 'upcoming' | 'past';
}

export interface PrivateLessonBookingsResponse {
  bookings: Array<PrivateLessonBooking>;
  timezone: string;
}

export interface BookPrivateLessonPayload {
  offeringId: string;
  studentId: string;
  startTime: string;
  endTime?: string;
  paymentMode: 'pay_later' | 'pay_now';
  notes?: string;
}

export type PrivateLessonRecurrenceFrequency =
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'custom';

export interface PrivateLessonRecurrenceRule {
  frequency: PrivateLessonRecurrenceFrequency;
  startDate: string;
  endDate?: string;
  occurrenceCount?: number;
  /** @deprecated Prefer daysOfWeek */
  dayOfWeek?: string;
  daysOfWeek?: string[];
  anchorStartTime?: string;
  customDates?: string[];
}

export interface PreviewPrivateLessonSeriesPayload {
  offeringId: string;
  studentId?: string;
  seriesId?: string;
  slots?: Array<{ startTime: string; endTime?: string }>;
  recurrence?: PrivateLessonRecurrenceRule;
}

export interface SeriesOccurrencePreview {
  startTime: string;
  endTime: string;
  date: string;
  status: 'available' | 'conflict' | 'past' | 'outside_window';
  conflictReason?: string;
}

export interface PreviewPrivateLessonSeriesResponse {
  seriesId: string;
  timezone: string;
  recurrenceLabel: string;
  occurrences: SeriesOccurrencePreview[];
  summary: { total: number; available: number; conflicts: number };
}

export interface BookPrivateLessonSeriesPayload
  extends PreviewPrivateLessonSeriesPayload {
  studentId: string;
  paymentMode: 'pay_later' | 'pay_now';
  notes?: string;
  skipConflicts?: boolean;
}

export interface SeriesBookSyncResult {
  async: false;
  seriesId: string;
  booked: Array<{ bookingId: string; startTime: string }>;
  skipped: Array<{ startTime: string; reason: string }>;
  failed: Array<{ startTime: string; reason: string }>;
}

export interface SeriesBookAsyncResult {
  async: true;
  jobId: string;
  seriesId: string;
  totalToBook: number;
}

export type BookPrivateLessonSeriesResponse =
  | SeriesBookSyncResult
  | SeriesBookAsyncResult;

export interface PrivateLessonSeriesJobStatus {
  jobId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';
  totalRecords: number;
  processedRecords: number;
  successfulBookings: number;
  skippedBookings: number;
  failedBookings: number;
  seriesId: string;
  result?: SeriesBookSyncResult;
  errorMessage?: string;
}

export const getPrivateLessonSlotsAPI = async (
  params: GetPrivateLessonSlotsParams,
) => {
  const response = await baseAPI.get<{
    slots: Array<PrivateLessonSlot>;
    timezone: string;
  }>('/private-lessons/available-slots', { params });
  return response.data;
};

export const useGetPrivateLessonSlots = (
  params: GetPrivateLessonSlotsParams,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: QUERY_KEYS.PRIVATE_LESSON_SLOTS(params),
    queryFn: () => getPrivateLessonSlotsAPI(params),
    enabled:
      options?.enabled ??
      Boolean(params.offeringId && params.startDate && params.endDate),
  });
};

export const getPrivateLessonBookingsAPI = async (
  params: GetPrivateLessonBookingsParams,
) => {
  const response = await baseAPI.get<PrivateLessonBookingsResponse>(
    '/private-lessons/bookings',
    { params },
  );
  return response.data;
};

export const useGetPrivateLessonBookings = (
  params: GetPrivateLessonBookingsParams,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.PRIVATE_LESSON_BOOKINGS(params),
    queryFn: () => getPrivateLessonBookingsAPI(params),
  });
};

export const bookPrivateLessonAPI = async (
  payload: BookPrivateLessonPayload,
) => {
  const response = await baseAPI.post<PrivateLessonBooking>(
    '/private-lessons/book',
    payload,
  );
  return response.data;
};

export const invalidatePrivateLessonQueries = () => {
  void queryClient.invalidateQueries({
    queryKey: [KEYS.PRIVATE_LESSON_BOOKINGS],
  });
  void queryClient.invalidateQueries({
    queryKey: [KEYS.PRIVATE_LESSON_SLOTS],
  });
  void queryClient.invalidateQueries({
    queryKey: [KEYS.CLASSES],
  });
};

export const useBookPrivateLesson = () => {
  return useMutation({
    mutationFn: bookPrivateLessonAPI,
    onSuccess: () => {
      invalidatePrivateLessonQueries();
    },
  });
};

export const previewPrivateLessonSeriesAPI = async (
  payload: PreviewPrivateLessonSeriesPayload,
) => {
  const response = await baseAPI.post<PreviewPrivateLessonSeriesResponse>(
    '/private-lessons/series/preview',
    payload,
  );
  return response.data;
};

export const usePreviewPrivateLessonSeries = () => {
  return useMutation({
    mutationFn: previewPrivateLessonSeriesAPI,
  });
};

export const bookPrivateLessonSeriesAPI = async (
  payload: BookPrivateLessonSeriesPayload,
) => {
  const response = await baseAPI.post<BookPrivateLessonSeriesResponse>(
    '/private-lessons/series/book',
    payload,
  );
  return response.data;
};

export const useBookPrivateLessonSeries = () => {
  return useMutation({
    mutationFn: bookPrivateLessonSeriesAPI,
    onSuccess: (result) => {
      // Async jobs only queue work — invalidate when the job completes instead.
      if (!result.async) {
        invalidatePrivateLessonQueries();
      }
    },
  });
};

export const getPrivateLessonSeriesJobAPI = async (jobId: string) => {
  const response = await baseAPI.get<PrivateLessonSeriesJobStatus>(
    `/private-lessons/series/jobs/${jobId}`,
  );
  return response.data;
};

export const useGetPrivateLessonSeriesJob = (
  jobId: string | null,
  options?: { enabled?: boolean; refetchInterval?: number },
) => {
  return useQuery({
    queryKey: ['private-lesson-series-job', jobId],
    queryFn: () => getPrivateLessonSeriesJobAPI(jobId!),
    enabled: (options?.enabled ?? true) && !!jobId,
    refetchInterval: options?.refetchInterval,
  });
};

export const cancelPrivateLessonBookingAPI = async (bookingId: string) => {
  const response = await baseAPI.post<PrivateLessonBooking>(
    `/private-lessons/bookings/${bookingId}/cancel`,
  );
  return response.data;
};

export const useCancelPrivateLessonBooking = () => {
  return useMutation({
    mutationFn: cancelPrivateLessonBookingAPI,
    onSuccess: () => {
      invalidatePrivateLessonQueries();
    },
  });
};

export const cancelPrivateLessonSeriesAPI = async (seriesId: string) => {
  const response = await baseAPI.post<{
    cancelledCount: number;
    cancelledBookingIds: string[];
  }>(`/private-lessons/series/${seriesId}/cancel-remaining`);
  return response.data;
};

export const useCancelPrivateLessonSeries = () => {
  return useMutation({
    mutationFn: cancelPrivateLessonSeriesAPI,
    onSuccess: () => {
      invalidatePrivateLessonQueries();
    },
  });
};
